import { addMonths, startOfMonth, subDays } from 'date-fns';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { formatCalendarDay, toCalendarDay } from '@adornis/datetime/calendarDay.js';

/**
 * Month boundaries of the job ticket.
 *
 * `jobTicketBestellDatum` and `jobTicketKuendigungsDatum` are month-granular: they are stored as the
 * canonical first day of the month they mean (00:00:00.000Z on the 1st, see
 * `@adornis/datetime/calendarDay.js`). Everything in here goes through the calendarDay helpers instead
 * of doing date arithmetic, because a canonical month start read or shifted in the browser's local
 * zone would land in the neighbouring month for anyone west of UTC.
 *
 * Note the offset baked into the cancel date: the user picks the *last valid month*, but the stored
 * `jobTicketKuendigungsDatum` is the month *after* it - so the ticket is valid from
 * `jobTicketBestellDatum` up to the end of the month before `jobTicketKuendigungsDatum`.
 */

/**
 * A plain, unproxied copy of a form value.
 *
 * The form document is an `on-change` proxy and hands out *proxied* `Date`s. Those are
 * `instanceof Date`, but `Object.prototype.toString` reports `[object Object]` for them and
 * `valueOf()` throws, so arithmetic and comparisons on them fail. Everything that leaves the form
 * (entity construction, the shared jobticket validators) therefore has to copy the value first.
 */
export const toPlainDate = (value: Maybe<Date>): Date | null => (value ? new Date(value.getTime()) : null);

/**
 * The canonical first day of the month an instant falls into.
 *
 * Legacy values were written as Berlin midnight, i.e. 22:00Z on the last day of the *previous*
 * month, so an instant is read as a German calendar day first - otherwise a ticket ordered "for
 * August" would show up as July.
 */
export const toCanonicalMonthStart = (value: Maybe<Date>): Date | null => {
  const day = toCalendarDay(toPlainDate(value));
  if (!day) return null;

  return toCalendarDay(startOfMonth(day));
};

/** The canonical month start `offset` months away from the month of `value`. */
export const shiftCanonicalMonths = (value: Date, offset: number): Date => {
  const day = toCalendarDay(new Date(value.getTime()));

  return toCalendarDay(startOfMonth(addMonths(day, offset)));
};

/** The canonical last day of the month before the month of `value`. */
export const canonicalEndOfPreviousMonth = (value: Date): Date => {
  const day = toCalendarDay(new Date(value.getTime()));

  return toCalendarDay(subDays(startOfMonth(day), 1));
};

/**
 * Stable identity of the month a value denotes, for change detection. Legacy and canonical
 * encodings of the same month produce the same identity.
 */
export const monthIdentity = (value: Maybe<Date>): string | null => {
  const monthStart = toCanonicalMonthStart(value);
  if (!monthStart) return null;

  return formatCalendarDay(monthStart, 'yyyy-MM');
};

/** True if both values denote the same month (or are both empty). */
export const isSameMonthValue = (a: Maybe<Date>, b: Maybe<Date>): boolean => monthIdentity(a) === monthIdentity(b);
